/**
 * useLifetimeStatus - Fetch lifetime subscription availability
 * 
 * Returns remaining slots for pricing page scarcity display.
 * Falls back to defaults if API unavailable.
 */

import { useState, useEffect } from 'react';

interface LifetimeStatus {
  remaining: number;
  total: number;
  soldOut: boolean;
}

const DEFAULT_STATUS: LifetimeStatus = {
  remaining: 89,
  total: 100,
  soldOut: false,
};

// Cache to avoid refetching on every component mount
let cachedStatus: LifetimeStatus | null = null;
let cacheTime = 0;
const CACHE_TTL = 60000; // 1 minute

export function useLifetimeStatus() {
  const [status, setStatus] = useState<LifetimeStatus>(cachedStatus || DEFAULT_STATUS);
  const [loading, setLoading] = useState(!cachedStatus);

  useEffect(() => {
    // Use cache if fresh
    if (cachedStatus && Date.now() - cacheTime < CACHE_TTL) {
      setStatus(cachedStatus);
      setLoading(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        const apiUrl = localStorage.getItem('ninjacash_api_url') || 'https://api.ninjacash.app';
        const res = await fetch(`${apiUrl}/api/lifetime-status`);
        
        if (res.ok) {
          const data = await res.json();
          cachedStatus = data;
          cacheTime = Date.now();
          setStatus(data);
        }
      } catch {
        // Use default on error
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  return { ...status, loading };
}
